// WorkerBundle: an eager, read-only mount over files that ship inside
// the Worker itself. The files can come from two places:
//
// - A directory under /bundle. With nodejs_compat, workerd exposes
//   every module in the upload as a read-only file there, readable
//   through node:fs. A module's name is its path, so a directory
//   survives only when wrangler uploads each file as its own module
//   under its relative path, which it does for files matched by
//   `rules` when `find_additional_modules` is on. A file imported from
//   code is renamed to a content hash instead.
//
// - A tree of paths to file contents, imported from code. That is what
//   Vite projects use, because @cloudflare/vite-plugin never puts
//   project files under /bundle, least of all under `vite dev`. The
//   tree is normally Vite's own `import.meta.glob(..., { query: "?raw" })`,
//   either written by hand or generated by computerBundle() from
//   @cloudflare/computer/vite, which also supplies a build-time
//   version. `?raw` reads files as UTF-8 text, so binary files don't
//   survive this path.
//
// node:fs calls in workerd are synchronous under the hood, so the
// directory path uses the sync API. That keeps WorkerBundle() itself
// synchronous, which MountFactory requires, while still letting it
// check the directory and compute a version at construction.
//
// The mount is always read-only. The deployment owns these files, so
// there is nowhere to write changes back to, and a redeploy replaces
// the copy (see `version` below).

import { createHash } from "node:crypto";
import { existsSync, readdirSync, readFileSync, statSync } from "node:fs";

import type { EagerMount, MountWriteAPI } from "../types.js";

export interface WorkerBundleEntry {
  // Relative to the bundled directory, forward slashes, no leading slash.
  readonly path: string;
  readonly type: "file" | "dir";
}

// File contents in a tree. Strings are written as UTF-8.
export type WorkerBundleFile = string | Uint8Array;

// Relative path to contents, or to a loader for them. The lazy form is
// what `import.meta.glob` returns without `eager: true`: each file is
// its own module, loaded only when the mount is copied in. Keys may
// start with "./" or "/", as glob keys do; both are stripped.
export type WorkerBundleTree = Readonly<
  Record<string, WorkerBundleFile | (() => Promise<WorkerBundleFile>)>
>;

// A tree with the version it was built from. computerBundle() produces
// this shape, so the Worker never has to load every file to compute one.
export interface WorkerBundleSource {
  readonly files: WorkerBundleTree;
  readonly version?: string;
}

export interface WorkerBundleOptions {
  // Return false to skip an entry. Skipping a directory skips its
  // whole subtree, and skipped entries don't count toward the version.
  filter?: (entry: WorkerBundleEntry) => boolean;
  // workerd's file system has no permission bits, so every bundled file
  // reads back the same way and scripts lose their executable bit.
  // Return a mode to set one, or undefined for the default: 0o755 for
  // files that start with "#!", 0o644 otherwise.
  fileMode?: (path: string, bytes: Uint8Array) => number | undefined;
  // Compared by the mount indexer with the version recorded at the last
  // index; a mismatch replaces the subtree. Defaults to the source's own
  // version when it has one, and otherwise to a SHA-256 of the included
  // paths, modes and bytes, computed once per isolate. With a filter or
  // fileMode, the hash is computed for each mount instead, and for a lazy
  // tree that means loading every file. Pass a string, such as a build
  // id, to skip hashing. Pass false to index once per store.
  version?: string | false;
  maxBytes?: number;
  maxEntries?: number;
}

// Lets tests point the provider at a stand-in for /bundle. Not
// exported from the package.
export interface WorkerBundleInternals {
  bundleDir: string;
}

type Filter = (entry: WorkerBundleEntry) => boolean;
type FileMode = (path: string, bytes: Uint8Array) => number;

// The two kinds of source behind one interface. `walk` yields entries
// depth-first in sorted order, each directory before its children, so
// materialize() and the hash always see the same sequence.
interface Reader {
  walk(filter: Filter): Generator<WorkerBundleEntry>;
  read(path: string): Uint8Array | Promise<Uint8Array>;
  // True when some reads are async, so the default hash must be too.
  readonly lazy: boolean;
  // The version the source declares, if any.
  readonly declaredVersion?: string;
  // Where the per-isolate hash cache keeps this source's entries.
  readonly cache: Map<string, string | Promise<string>>;
}

const DEFAULT_BUNDLE_DIR = "/bundle";
// Under `vite dev` the Worker runs through Vite's module runner, and
// /bundle holds only the runner's own modules, this one among them.
// None of the project's files are there, so its presence turns a
// confusing "does not exist" into an explanation.
const VITE_DEV_MARKER = "__VITE_WORKER_ENTRY__";

// A source's contents can't change while an isolate is alive: /bundle
// is fixed for the deployment, and a tree is a module's export, which
// Vite replaces with a new object when it reloads the module. So a hash
// computed once holds for every durable object the isolate hosts.
// Directories are keyed by path, trees by object identity. Only hashes
// that depend on nothing but the source are cached here; with a filter
// or fileMode, each mount computes its own (see resolveVersion).
const directoryCaches = new Map<string, Map<string, string | Promise<string>>>();
const treeCaches = new WeakMap<object, Map<string, string | Promise<string>>>();

export function WorkerBundle(
  source: string | WorkerBundleTree | WorkerBundleSource,
  options: WorkerBundleOptions = {},
): EagerMount {
  return createWorkerBundle(source, options, { bundleDir: DEFAULT_BUNDLE_DIR });
}

export function createWorkerBundle(
  source: string | WorkerBundleTree | WorkerBundleSource,
  options: WorkerBundleOptions,
  internals: WorkerBundleInternals,
): EagerMount {
  const reader =
    typeof source === "string"
      ? directoryReader(resolveBundlePath(source, internals.bundleDir), internals.bundleDir)
      : treeReader(source);

  const filter = options.filter ?? (() => true);
  const fileMode: FileMode = (relPath, bytes) =>
    options.fileMode?.(relPath, bytes) ?? defaultFileMode(bytes);

  return {
    kind: "worker-bundle",
    strategy: "eager",
    mode: "read-only",
    maxBytes: options.maxBytes,
    maxEntries: options.maxEntries,
    version: resolveVersion(reader, options, filter, fileMode),
    async materialize(api: MountWriteAPI): Promise<void> {
      for (const entry of reader.walk(filter)) {
        const target = `${api.root}/${entry.path}`;
        if (entry.type === "dir") {
          await api.mkdir(target);
          continue;
        }
        const bytes = await reader.read(entry.path);
        await api.writeFile(target, singleChunk(bytes), fileMode(entry.path, bytes));
      }
    },
  };
}

// Relative paths are read from /bundle. Absolute paths are used as-is,
// which is how tests and Node callers point at a directory on disk.
function resolveBundlePath(path: string, bundleDir: string): string {
  if (path.length === 0) {
    throw new Error("WorkerBundle: path must not be empty");
  }
  const segments = path.split("/");
  if (segments.includes("..")) {
    throw new Error(`WorkerBundle: path must not contain '..' segments: ${JSON.stringify(path)}`);
  }
  const absolute = path.startsWith("/") ? path : `${bundleDir}/${path}`;
  const normalized = absolute
    .split("/")
    .filter((s) => s.length > 0 && s !== ".")
    .join("/");
  return `/${normalized}`;
}

function directoryReader(root: string, bundleDir: string): Reader {
  assertDirectory(root, bundleDir);
  let cache = directoryCaches.get(root);
  if (cache === undefined) {
    cache = new Map();
    directoryCaches.set(root, cache);
  }
  return {
    lazy: false,
    cache,
    walk: (filter) => walkDirectory(root, "", filter),
    read: (path) => readBytes(`${root}/${path}`),
  };
}

// A missing directory almost always means the wrangler rule is
// missing, or that a Vite project passed a path instead of a tree, so
// fail when the durable object starts, with the fix in the message,
// rather than mount an empty directory.
function assertDirectory(root: string, bundleDir: string): void {
  let isDirectory = false;
  let exists = false;
  try {
    const stat = statSync(root);
    exists = true;
    isDirectory = stat.isDirectory();
  } catch {
    exists = false;
  }
  if (isDirectory) return;
  if (exists) {
    throw new Error(`WorkerBundle: ${root} is not a directory`);
  }
  const name = root.startsWith(`${bundleDir}/`) ? root.slice(bundleDir.length + 1) : root;
  const viteFix =
    `add computerBundle() from @cloudflare/computer/vite to your plugins, then ` +
    `\`import files from "computer:bundle/src/${name}"\` and pass WorkerBundle(files).`;
  if (existsSync(`${bundleDir}/${VITE_DEV_MARKER}`)) {
    throw new Error(
      `WorkerBundle: ${root} is not available under \`vite dev\`, which never puts project ` +
        `files under /bundle. To ship files from a Vite project, ${viteFix}`,
    );
  }
  throw new Error(
    `WorkerBundle: ${root} does not exist. Files only appear under /bundle when they are ` +
      'uploaded as modules. With wrangler, set "find_additional_modules": true and add a rule ' +
      `such as { "type": "Data", "globs": ["${name}/**/*"] } in wrangler.jsonc. With Vite, ${viteFix}`,
  );
}

function* walkDirectory(root: string, rel: string, filter: Filter): Generator<WorkerBundleEntry> {
  const dir = rel === "" ? root : `${root}/${rel}`;
  const dirents = readdirSync(dir, { withFileTypes: true }).sort((a, b) =>
    compareNames(a.name, b.name),
  );
  for (const dirent of dirents) {
    const path = rel === "" ? dirent.name : `${rel}/${dirent.name}`;
    if (dirent.isDirectory()) {
      const entry: WorkerBundleEntry = { path, type: "dir" };
      if (!filter(entry)) continue;
      yield entry;
      yield* walkDirectory(root, path, filter);
    } else if (dirent.isFile()) {
      const entry: WorkerBundleEntry = { path, type: "file" };
      if (filter(entry)) yield entry;
    }
  }
}

function treeReader(source: WorkerBundleTree | WorkerBundleSource): Reader {
  const isSource = isWorkerBundleSource(source);
  const files: WorkerBundleTree = isSource ? source.files : source;
  // Normalized path → original key, plus each directory's children, so
  // the tree can be walked like a directory on disk.
  const keys = new Map<string, string>();
  const children = new Map<string, Map<string, "file" | "dir">>([["", new Map()]]);
  let lazy = false;
  for (const key of Object.keys(files)) {
    const path = normalizeTreeKey(key);
    if (keys.has(path)) {
      throw new Error(`WorkerBundle: tree has two keys for ${JSON.stringify(path)}`);
    }
    keys.set(path, key);
    if (typeof files[key] === "function") lazy = true;
    const segments = path.split("/");
    for (let i = 0; i < segments.length; i++) {
      const parent = segments.slice(0, i).join("/");
      const self = segments.slice(0, i + 1).join("/");
      const type = i === segments.length - 1 ? "file" : "dir";
      const siblings = children.get(parent) ?? new Map();
      children.set(parent, siblings);
      const existing = siblings.get(segments[i]);
      if (existing !== undefined && existing !== type) {
        throw new Error(
          `WorkerBundle: tree uses ${JSON.stringify(self)} as both a file and a directory`,
        );
      }
      siblings.set(segments[i], type);
      if (type === "dir" && !children.has(self)) children.set(self, new Map());
    }
  }

  let cache = treeCaches.get(source);
  if (cache === undefined) {
    cache = new Map();
    treeCaches.set(source, cache);
  }

  function* walk(rel: string, filter: Filter): Generator<WorkerBundleEntry> {
    const names = [...(children.get(rel)?.entries() ?? [])].sort(([a], [b]) => compareNames(a, b));
    for (const [name, type] of names) {
      const path = rel === "" ? name : `${rel}/${name}`;
      const entry: WorkerBundleEntry = { path, type };
      if (!filter(entry)) continue;
      yield entry;
      if (type === "dir") yield* walk(path, filter);
    }
  }

  return {
    lazy,
    cache,
    declaredVersion: isSource ? source.version : undefined,
    walk: (filter) => walk("", filter),
    read(path) {
      const key = keys.get(path);
      if (key === undefined) throw new Error(`WorkerBundle: no file at ${JSON.stringify(path)}`);
      const value = files[key];
      return typeof value === "function" ? value().then(toBytes) : toBytes(value);
    },
  };
}

// A tree's values are never plain objects, so an object whose `files`
// is a plain object can only be a WorkerBundleSource.
function isWorkerBundleSource(
  source: WorkerBundleTree | WorkerBundleSource,
): source is WorkerBundleSource {
  const files = (source as { files?: unknown }).files;
  return typeof files === "object" && files !== null && !(files instanceof Uint8Array);
}

function normalizeTreeKey(key: string): string {
  const segments = key
    .replaceAll("\\", "/")
    .split("/")
    .filter((s) => s.length > 0 && s !== ".");
  if (segments.length === 0) {
    throw new Error(`WorkerBundle: tree key ${JSON.stringify(key)} has no path`);
  }
  if (segments.includes("..")) {
    throw new Error(
      `WorkerBundle: tree key must not contain '..' segments: ${JSON.stringify(key)}`,
    );
  }
  return segments.join("/");
}

function toBytes(value: WorkerBundleFile): Uint8Array {
  if (typeof value === "string") return new TextEncoder().encode(value);
  if (value instanceof Uint8Array) return value;
  throw new Error(
    `WorkerBundle: file contents must be a string or Uint8Array, got ${typeof value}`,
  );
}

function resolveVersion(
  reader: Reader,
  options: WorkerBundleOptions,
  filter: Filter,
  fileMode: FileMode,
): string | (() => Promise<string>) | undefined {
  if (options.version === false) return undefined;
  if (typeof options.version === "string") return options.version;

  // filter and fileMode are closures, and two with the same source can
  // still select different files or modes (one per session, say), so a
  // version that depends on them is never shared between mounts.
  const hasCallbacks = options.filter !== undefined || options.fileMode !== undefined;

  if (reader.declaredVersion !== undefined) {
    if (!hasCallbacks) return reader.declaredVersion;
    // A declared version only describes the files. A filter changes
    // which of them land, and the selected paths are known without
    // loading anything, so fold those in.
    if (options.fileMode === undefined) {
      const hash = createHash("sha256").update(`${reader.declaredVersion}\0`);
      for (const entry of reader.walk(filter)) hash.update(`${entry.type}\0${entry.path}\0`);
      return `sha256:${hash.digest("hex")}`;
    }
    // fileMode sees each file's bytes, so only a full hash covers it.
  }

  if (!hasCallbacks) {
    const cached = reader.cache.get("");
    if (typeof cached === "string") return cached;
  }
  if (!reader.lazy) {
    const version = hashSync(reader, filter, fileMode);
    if (!hasCallbacks) reader.cache.set("", version);
    return version;
  }
  // A lazy tree can only be hashed by loading every file, which is
  // async, so hand the indexer a function, and load at most once per
  // isolate when the result can be shared, or once per mount otherwise.
  let own: Promise<string> | undefined;
  return () => {
    if (hasCallbacks) {
      if (own === undefined) {
        own = hashAsync(reader, filter, fileMode);
        // Forget a failure so the indexer's next pass loads again,
        // rather than replaying the same rejection.
        own.catch(() => {
          own = undefined;
        });
      }
      return own;
    }
    let pending = reader.cache.get("");
    if (pending === undefined) {
      pending = hashAsync(reader, filter, fileMode);
      reader.cache.set("", pending);
      pending.then(
        (version) => reader.cache.set("", version),
        () => reader.cache.delete(""),
      );
    }
    return Promise.resolve(pending);
  };
}

function hashSync(reader: Reader, filter: Filter, fileMode: FileMode): string {
  const hash = createHash("sha256");
  for (const entry of reader.walk(filter)) {
    hash.update(`${entry.type}\0${entry.path}\0`);
    if (entry.type === "file")
      hashFile(hash, entry.path, reader.read(entry.path) as Uint8Array, fileMode);
  }
  return `sha256:${hash.digest("hex")}`;
}

async function hashAsync(reader: Reader, filter: Filter, fileMode: FileMode): Promise<string> {
  const hash = createHash("sha256");
  for (const entry of reader.walk(filter)) {
    hash.update(`${entry.type}\0${entry.path}\0`);
    if (entry.type === "file") hashFile(hash, entry.path, await reader.read(entry.path), fileMode);
  }
  return `sha256:${hash.digest("hex")}`;
}

function hashFile(
  hash: ReturnType<typeof createHash>,
  path: string,
  bytes: Uint8Array,
  fileMode: FileMode,
): void {
  // The mode is part of the copy, so a fileMode change has to trigger a
  // refresh just like a content change.
  hash.update(`${fileMode(path, bytes).toString(8)}\0`);
  hash.update(bytes);
  hash.update("\0");
}

function compareNames(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function readBytes(absPath: string): Uint8Array {
  const buf = readFileSync(absPath);
  return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
}

function defaultFileMode(bytes: Uint8Array): number {
  return bytes[0] === 0x23 && bytes[1] === 0x21 ? 0o755 : 0o644;
}

// MountWriteAPI takes a stream so large sources can flow through
// without buffering. Bundled files are already in memory, so one chunk
// is enough.
function singleChunk(bytes: Uint8Array): ReadableStream<Uint8Array> {
  return new ReadableStream<Uint8Array>({
    start(controller) {
      controller.enqueue(bytes);
      controller.close();
    },
  });
}
